// The second sign-in step on screen (Sahil, 2026-10-23): the gate that sits
// between email + password and the app, and the "Authenticator app" card in
// the profile. The rules and the Supabase calls live in ../utils/mfa.
//
// Replaced the passkey screens the same day (Supabase will not enable
// WebAuthn as a second factor); the code from an authenticator app is the
// second step now.
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabase";
import { Btn, Input, Select } from "../ui";
import { BrandMark } from "./BrandMark";
import { mfaState, startEnrollment, finishEnrollment, cancelEnrollment, verifyCode, removeFactor, canRemoveFactor, defaultFactorName, normalizeCode, trustThisDevice, TRUST_DAYS } from "../utils/mfa";
import { setStepUpHandler, requestStepUp } from "../utils/stepUp";
import { Modal } from "./shared";
import { fmtDate } from "../utils/helpers";

const ua = () => (typeof navigator !== "undefined" ? navigator.userAgent : "");
// A phone or tablet: the authenticator app is on THIS device, so a QR code
// shown here cannot be scanned (2026-10-24). iPadOS reports itself as a Mac,
// hence the touch check.
export const isPhoneLike = (agent = ua(), touchPoints = (typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0)) =>
  /iPhone|iPod|Android|Mobile/i.test(agent) || (/Macintosh/.test(agent) && touchPoints > 1);

// The six-digit box. Digits only; submits itself the moment six are in.
function CodeInput({ value, onChange, onComplete, disabled, autoFocus = true, id = "mfa-code" }) {
  return (
    <Input id={id} value={value} disabled={disabled} autoFocus={autoFocus}
      inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} placeholder="123456" aria-label="Six-digit code"
      className="w-full text-center text-2xl tracking-[0.5em] font-mono"
      onChange={e => { const v = normalizeCode(e.target.value); onChange(v); if (v.length === 6 && onComplete) onComplete(v); }}
      onKeyDown={e => { if (e.key === "Enter" && value.length === 6 && onComplete) onComplete(value); }} />
  );
}

// Setting up an authenticator, in two steps: name → QR code + secret + first
// code. Used by the gate (required people at first sign-in) and the profile
// card. onDone() once the code is accepted; onCancel() to drop the half-made
// factor.
function EnrollForm({ takenNames = [], onDone, onCancel, compact = false }) {
  const [name, setName] = useState(() => defaultFactorName(ua(), takenNames));
  const [setup, setSetup] = useState(null); // { factorId, qrCode, secret, uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const submitting = useRef(false);

  async function start() {
    setBusy(true); setErr("");
    const r = await startEnrollment(supabase, name);
    setBusy(false);
    if (!r.ok) { setErr(r.error); return; }
    setSetup({ factorId: r.factorId, qrCode: r.qrCode, secret: r.secret, uri: r.uri });
  }

  async function finish(c) {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setErr("");
    const r = await finishEnrollment(supabase, setup.factorId, c || code);
    setBusy(false);
    submitting.current = false;
    if (r.ok) { onDone(); return; }
    setErr(r.error);
    setCode("");
  }

  async function back() {
    setBusy(true);
    await cancelEnrollment(supabase, setup?.factorId);
    setBusy(false);
    setSetup(null); setCode(""); setErr("");
    if (onCancel) onCancel();
  }

  // Groups of four read more easily when typing the secret by hand.
  const secretGroups = (setup?.secret || "").replace(/(.{4})/g, "$1 ").trim();
  const phone = isPhoneLike();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  async function copyKey() {
    let ok = false;
    try { await navigator.clipboard.writeText(setup.secret); ok = true; } catch (_) { /* older browsers: the key stays selectable */ }
    setCopied(ok);
    if (!ok) setShowSecret(true);
  }
  const keyBlock = (
    <div className="mt-1 flex items-center justify-center gap-2 flex-wrap">
      <span className="block w-full text-center font-mono text-sm text-neutral-700 break-words select-all" data-testid="mfa-secret">{secretGroups}</span>
      <Btn variant="secondary" size="xs" onClick={copyKey} data-testid="mfa-copy-key">{copied ? "Copied" : "Copy key"}</Btn>
    </div>
  );

  if (!setup) return (
    <div className="text-left">
      <label className="text-xs font-medium text-neutral-600 mb-1 block" htmlFor="mfa-name">Name for this authenticator</label>
      <Input id="mfa-name" value={name} onChange={e => setName(e.target.value)} maxLength={60} placeholder="e.g. My phone" />
      <Btn variant="primary" className={"w-full mt-3"} onClick={start} disabled={busy || !name.trim()}>{busy ? "Preparing…" : phone ? "Continue" : "Show QR code"}</Btn>
      {onCancel && <Btn variant="ghost" size="sm" className="w-full mt-2" onClick={onCancel} disabled={busy}>Cancel</Btn>}
      {err && <div role="alert" className="mt-3 text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">{err}</div>}
    </div>
  );

  const qrImg = setup.qrCode
    ? <img src={setup.qrCode} alt="QR code for your authenticator app" width={compact ? 160 : 200} height={compact ? 160 : 200} className="mx-auto border border-neutral-200 rounded-lg bg-white p-1" />
    : <p className="text-sm text-neutral-500">No QR code could be drawn. Copy the key into your app instead.</p>;

  return (
    <div className="text-left" data-testid="mfa-enroll">
      {phone ? <>
        {/* On a phone: hand the secret straight to the authenticator app
            (the otpauth:// link it registers for), or copy the key. */}
        <ol className="text-sm text-neutral-600 space-y-1 mb-3 list-decimal list-inside">
          <li>Tap <strong>Add to authenticator app</strong>. Your phone opens Apple Passwords, Google Authenticator or the app you use, with Housify filled in.</li>
          <li>Come back here and enter the six-digit code it shows.</li>
        </ol>
        {setup.uri && <a href={setup.uri} data-testid="mfa-open-app" className="flex items-center justify-center gap-2 w-full rounded-xl bg-brand-600 text-white font-semibold py-3 min-h-[44px]">
          <span className="material-icons-outlined text-base">key</span>Add to authenticator app</a>}
        <p className="text-xs text-neutral-500 mt-3 text-center">Nothing opened? Copy the key and paste it into your app as a new account.</p>
        {keyBlock}
        <div className="mt-2 text-center">
          <button type="button" className="text-xs text-brand-600 underline" onClick={() => setShowQr(v => !v)}>{showQr ? "Hide the QR code" : "Authenticator on another device? Show the QR code"}</button>
          {showQr && <div className="mt-2">{qrImg}</div>}
        </div>
      </> : <>
        <ol className="text-sm text-neutral-600 space-y-1 mb-3 list-decimal list-inside">
          <li>Open your authenticator app (Google Authenticator, Authy, 1Password, Apple Passwords…).</li>
          <li>Scan this code, or copy the key into the app.</li>
          <li>Enter the six-digit code the app shows.</li>
        </ol>
        {qrImg}
        <div className="mt-2 text-center">
          <button type="button" className="text-xs text-brand-600 underline" onClick={() => setShowSecret(v => !v)}>{showSecret ? "Hide the key" : "Can't scan? Show the key"}</button>
          {showSecret && keyBlock}
        </div>
      </>}
      <label className="text-xs font-medium text-neutral-600 mt-4 mb-1 block" htmlFor="mfa-code">Code from the app</label>
      <CodeInput value={code} onChange={setCode} onComplete={finish} disabled={busy} />
      <Btn variant="primary" className="w-full mt-3" onClick={() => finish()} disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Verify and finish"}</Btn>
      <Btn variant="ghost" size="sm" className="w-full mt-2" onClick={back} disabled={busy}>Start over</Btn>
      {err && <div role="alert" className="mt-3 text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">{err}</div>}
    </div>
  );
}

// Shown right after sign-in, before the company picker and the app shell.
//   verify  they have an authenticator: "Enter your code"
//   enroll  one is required and they have none: "Set up your authenticator"
//   error   the check itself failed: try again, or sign out
// onDone runs once the session is aal2 (or the check says no gate is needed).
export function MfaGate({ user, initial, onDone: onPassed, onSignOut }) {
  const [st, setSt] = useState(initial || { mode: "error", factors: [] });
  // A good code (or a finished setup) trusts this browser for TRUST_DAYS
  // before going on; a failure to remember it never blocks the sign-in.
  const onDone = async () => { await trustThisDevice(supabase).catch(() => false); onPassed(); };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [factorId, setFactorId] = useState(() => (initial?.factors || [])[0]?.id || "");
  const submitting = useRef(false);

  async function submitCode(c) {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setErr("");
    const r = await verifyCode(supabase, factorId || null, c || code);
    setBusy(false);
    submitting.current = false;
    if (r.ok) { onDone(); return; }
    setErr(r.error);
    setCode("");
  }

  async function retry() {
    setBusy(true); setErr("");
    const next = await mfaState(supabase, user).catch(e => ({ mode: "error", factors: [], error: String(e?.message || e) }));
    setBusy(false);
    if (next.mode === "none") { onDone(); return; }
    setSt(next);
    setFactorId((next.factors || [])[0]?.id || "");
    if (next.mode === "error") setErr(next.error);
  }

  useEffect(() => { if (!factorId && (st.factors || []).length) setFactorId(st.factors[0].id); }, [st.factors, factorId]);

  const factors = st.factors || [];
  return (
    <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex items-center justify-center p-4" data-testid="mfa-gate">
    <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-8 w-full max-w-sm text-center">
    <BrandMark size={40} className="mx-auto mb-3" />
    {st.mode === "verify" && <>
      <h2 className="text-xl font-bold text-neutral-800 mb-1">Enter your code</h2>
      <p className="text-sm text-neutral-500 mb-4">Open your authenticator app and type the six-digit code it shows for Housify.{st.trustReason === "new_country" ? " You're signing in from a different country than usual." : st.trustReason === "expired" ? ` It's been ${TRUST_DAYS} days since your last code on this device.` : ""}</p>
      {factors.length > 1 && (
        <div className="text-left mb-3">
          <label className="text-xs font-medium text-neutral-600 mb-1 block" htmlFor="mfa-factor">Which authenticator?</label>
          <Select id="mfa-factor" value={factorId} onChange={e => setFactorId(e.target.value)}>
            {factors.map(f => <option key={f.id} value={f.id}>{f.friendly_name || "Authenticator"}</option>)}
          </Select>
        </div>
      )}
      <CodeInput value={code} onChange={setCode} onComplete={submitCode} disabled={busy} />
      <Btn variant="primary" className="w-full mt-3" onClick={() => submitCode()} disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Continue"}</Btn>
      <p className="text-xs text-neutral-400 mt-4">This device will be remembered for {TRUST_DAYS} days. Lost the phone with your authenticator? Ask an admin of your company to reset it.</p>
    </>}
    {st.mode === "enroll" && <>
      <h2 className="text-xl font-bold text-neutral-800 mb-1">Set up your authenticator</h2>
      <p className="text-sm text-neutral-500 mb-4 text-left">You're an admin, and admins can connect the company's bank accounts, so Housify asks for a code from an authenticator app: once every {TRUST_DAYS} days on each device, and before any bank change. A stolen password alone will not get anyone in.</p>
      <EnrollForm onDone={onDone} />
    </>}
    {st.mode === "error" && <>
      <h2 className="text-xl font-bold text-neutral-800 mb-1">We couldn't check your sign-in</h2>
      <p className="text-sm text-neutral-500 mb-6">Check your connection and try again.</p>
      <Btn variant="primary" className="w-full" onClick={retry} disabled={busy}>{busy ? "Checking…" : "Try again"}</Btn>
    </>}
    {err && <div role="alert" className="mt-4 text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2 text-left">{err}</div>}
    <Btn variant="ghost" size="sm" className="mt-4 mx-auto" onClick={onSignOut} disabled={busy}>Sign out</Btn>
    </div>
    </div>
  );
}

// The profile's "Authenticator app" card. Staff only: tenant and owner
// portal users do not use it. A person who must use one cannot remove their last.
export function MfaPanel({ currentUser, showToast, showConfirm }) {
  const [st, setSt] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    const next = await mfaState(supabase, currentUser).catch(e => ({ mode: "error", factors: [], staff: false, error: String(e?.message || e) }));
    setSt(next);
  }
  useEffect(() => { if (currentUser?.email) load(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.email]);

  // Supabase lets a session change its authenticators only after a code
  // (aal2). A trusted device signs in without one, so ask first.
  async function ensureVerified(why) {
    if (st.aal === "aal2" || !(st.factors || []).length) return true;
    const ok = await requestStepUp(why);
    if (ok) await load();
    return ok;
  }

  async function remove(f) {
    if (!canRemoveFactor({ required: st.required, factors: st.factors })) return;
    if (!await ensureVerified("remove an authenticator")) return;
    if (!await showConfirm({ message: `Remove the authenticator "${f.friendly_name || "Authenticator"}"?\n\nCodes from that app entry will no longer be accepted.`, variant: "danger", confirmText: "Remove" })) return;
    setBusy(true);
    const r = await removeFactor(supabase, f.id);
    setBusy(false);
    if (!r.ok) { showToast(r.error, "error"); return; }
    showToast("Authenticator removed.", "success");
    load();
  }

  if (!st) return null;
  if (!st.staff && !(st.factors || []).length) return null;
  const factors = st.factors || [];
  const lastLocked = !canRemoveFactor({ required: st.required, factors });
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-6 mb-4" data-testid="mfa-panel">
    <h3 className="font-semibold text-neutral-800 mb-2">Authenticator app</h3>
    <p className="text-xs text-neutral-400 mb-3">
      {st.required
        ? `You're an admin, and admins can connect the company's bank accounts, so a code from an authenticator app is required: once every ${TRUST_DAYS} days on each device, and before any bank change.`
        : `Optional. With an authenticator app, you confirm it's you with a six-digit code once every ${TRUST_DAYS} days on each device.`}
    </p>
    {st.mode === "error" && <div className="text-xs text-danger-600 mb-3">{st.error}</div>}
    {factors.length > 0 && (
      <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg mb-3">
      {factors.map(f => (
        <div key={f.id} className="flex items-center justify-between px-3 py-2">
        <div>
        <div className="text-sm text-neutral-700">{f.friendly_name || "Authenticator"}</div>
        <div className="text-xs text-neutral-400">Added {fmtDate(f.created_at)}</div>
        </div>
        <Btn variant="danger" size="xs" onClick={() => remove(f)} disabled={busy || lastLocked} title={lastLocked ? "Your only authenticator can't be removed" : undefined}>Remove</Btn>
        </div>
      ))}
      </div>
    )}
    {lastLocked && <p className="text-xs text-neutral-500 mb-3">This is your only authenticator, and you need one to sign in, so it can't be removed. Add another first, or ask an admin to reset it.</p>}
    {adding
      ? <EnrollForm compact takenNames={factors.map(f => f.friendly_name)} onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); showToast(`Authenticator added. You'll enter its code once every ${TRUST_DAYS} days on each device.`, "success"); load(); }} />
      : <Btn size="sm" onClick={async () => { if (await ensureVerified("add an authenticator")) setAdding(true); }} disabled={busy}>Add an authenticator</Btn>}
    </div>
  );
}

// The code prompt for step-up (2026-10-24): mounted once in the signed-in
// app. utils/stepUp.js requestStepUp() opens it when a bank action or a
// stored-login reveal needs a verified session; it resolves true once a
// code is accepted (the session is then aal2) and the device is re-trusted.
export function StepUpHost() {
  const [ask, setAsk] = useState(null); // { reason, resolve }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submitting = useRef(false);

  useEffect(() => {
    setStepUpHandler((reason) => new Promise(resolve => { setCode(""); setErr(""); setAsk({ reason, resolve }); }));
    return () => setStepUpHandler(null);
  }, []);

  function close(result) { if (ask) ask.resolve(result); setAsk(null); setCode(""); setErr(""); }

  async function submit(c) {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setErr("");
    const r = await verifyCode(supabase, null, c || code);
    if (r.ok) await trustThisDevice(supabase).catch(() => false);
    setBusy(false);
    submitting.current = false;
    if (r.ok) { close(true); return; }
    setErr(r.error); setCode("");
  }

  if (!ask) return null;
  return (
    <Modal title="Enter your code" onClose={() => close(false)}>
      <div data-testid="mfa-stepup">
        <p className="text-sm text-neutral-600 mb-4">To {ask.reason || "do this"}, enter the six-digit code from your authenticator app.</p>
        <CodeInput id="mfa-stepup-code" value={code} onChange={setCode} onComplete={submit} disabled={busy} />
        {err && <div role="alert" className="mt-3 text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="ghost" size="sm" onClick={() => close(false)} disabled={busy}>Cancel</Btn>
          <Btn size="sm" onClick={() => submit()} disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Continue"}</Btn>
        </div>
      </div>
    </Modal>
  );
}
